/* covered.ro — horizontal rail: the track is a native snap scroller (swipe on touch,
   trackpad on desktop, works without JS); this adds the prev/next buttons, mouse drag
   and the editor's block select. Used by cv-collections-rail and cv-drop.
   Markup: <cv-rail> > [data-cv-rail-track] (the scroller, its children are the items)
   and, anywhere inside, optional [data-cv-rail-prev] / [data-cv-rail-next] buttons.
   The host gets .is-scrollable while the track overflows (CSS shows the buttons then). */
(() => {
  if (customElements.get('cv-rail')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class CvRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cv-rail-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-cv-rail-prev]');
      this.next = this.querySelector('[data-cv-rail-next]');

      this.onScroll = this.onScroll.bind(this);
      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);
      this.onDragStart = (event) => event.preventDefault();
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('click', this.onClickCapture, true);
      this.track.addEventListener('dragstart', this.onDragStart);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      if (this.prev) this.prev.addEventListener('click', this.onPrev);
      if (this.next) this.next.addEventListener('click', this.onNext);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.track);
      } else {
        this.onResize = () => this.update();
        window.addEventListener('resize', this.onResize, { passive: true });
      }
      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('click', this.onClickCapture, true);
      this.track.removeEventListener('dragstart', this.onDragStart);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.prev) this.prev.removeEventListener('click', this.onPrev);
      if (this.next) this.next.removeEventListener('click', this.onNext);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      this.endDrag();
    }

    get behavior() {
      return reduceMotion.matches || document.body.classList.contains('disable-animations') ? 'auto' : 'smooth';
    }

    get max() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const max = this.max;
      const left = this.track.scrollLeft;
      const scrollable = max > 2;
      this.classList.toggle('is-scrollable', scrollable);
      if (this.prev) this.prev.disabled = !scrollable || left <= 2;
      if (this.next) this.next.disabled = !scrollable || left >= max - 2;
    }

    // Snap positions: each item's start, minus the track's scroll padding (the page gutter).
    targets() {
      const style = getComputedStyle(this.track);
      const pad = parseFloat(style.scrollPaddingLeft) || 0;
      const origin = this.track.getBoundingClientRect().left + pad - this.track.scrollLeft;
      const max = this.max;
      return Array.from(this.track.children, (item) =>
        Math.min(max, Math.max(0, Math.round(item.getBoundingClientRect().left - origin)))
      );
    }

    step(direction) {
      const left = this.track.scrollLeft;
      const targets = this.targets();
      let target = direction > 0 ? targets.find((x) => x > left + 2) : targets.reverse().find((x) => x < left - 2);
      if (target === undefined) target = direction > 0 ? this.max : 0;
      this.track.scrollTo({ left: target, behavior: this.behavior });
    }

    snapNearest() {
      const left = this.track.scrollLeft;
      const targets = this.targets();
      if (!targets.length) return;
      const nearest = targets.reduce((best, x) => (Math.abs(x - left) < Math.abs(best - left) ? x : best), targets[0]);
      this.track.scrollTo({ left: nearest, behavior: this.behavior });
    }

    // Mouse drag (touch and pen keep the native swipe). A drag of more than 6px cancels
    // the click that follows, so letting go over a card does not open it.
    onPointerDown(event) {
      this.dragged = false;
      if (event.pointerType !== 'mouse' || event.button !== 0 || this.max <= 2) return;
      this.drag = { id: event.pointerId, x: event.clientX, left: this.track.scrollLeft, moved: false };
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) <= 6) return;
        drag.moved = true;
        this.track.classList.add('is-dragging');
      }
      event.preventDefault();
      this.track.scrollLeft = drag.left - dx;
    }

    onPointerUp(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const moved = drag.moved;
      this.endDrag();
      if (moved) {
        // The click (if any) is dispatched right after pointerup, in the same task.
        this.dragged = true;
        setTimeout(() => {
          this.dragged = false;
        }, 0);
        this.snapNearest();
      }
    }

    endDrag() {
      this.drag = null;
      this.track.classList.remove('is-dragging');
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    onClickCapture(event) {
      if (!this.dragged) return;
      this.dragged = false;
      event.preventDefault();
      event.stopPropagation();
    }

    onBlockSelect(event) {
      const item = Array.from(this.track.children).find((child) => child.contains(event.target));
      if (!item) return;
      const index = Array.from(this.track.children).indexOf(item);
      const target = this.targets()[index];
      if (target !== undefined) this.track.scrollTo({ left: target, behavior: this.behavior });
    }
  }

  customElements.define('cv-rail', CvRail);
})();
